import { WebRequest } from '@analyzer/core/lib/collector/collectors/web.js';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  Alert as MuiAlert,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { blue, green, grey, orange, red } from '@mui/material/colors';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAnalysisDebugQuery } from '../api/analysis.js';
import { AnalysisType } from '@analyzer/client';

const truncateUrl = (url: string, length = 128) => {
  try {
    const temp = new URL(url);
    const t = temp.origin + temp.pathname;
    return t.length > length ? `${t.slice(0, length)}...` : t;
  } catch {
    return url.length > length ? `${url.slice(0, length)}...` : url;
  }
};

const formatPercent = (num: number) => `${(num * 100).toFixed(2)}%`;

const Pill = ({ label, color }: { label: string; color: string }) => (
  <Chip label={label} size="small" sx={{ bgcolor: color, color: 'white' }} />
);

const RequestRow = ({
  req,
  matchUrl,
  matchContent
}: {
  req: WebRequest;
  matchUrl: boolean;
  matchContent: boolean;
}) => (
  <Stack direction="row" spacing={1} alignItems="center" py={0.5} flexWrap="wrap">
    <Pill label={req.request.method} color={blue[600]} />
    <Tooltip title={req.request.url}>
      <Chip
        label={truncateUrl(req.request.url)}
        size="small"
        sx={{ bgcolor: matchUrl ? green[500] : red[500], color: 'white' }}
      />
    </Tooltip>
    <Chip label={req.response.contentType} size="small" />
    <Tooltip title={`SHA256: ${req.response.sha256}`}>
      <Chip
        label={req.response.sha256.slice(0, 8)}
        size="small"
        sx={{ bgcolor: matchContent ? green[700] : orange[700], color: 'white' }}
      />
    </Tooltip>
    <MuiLink
      component={RouterLink}
      to={'aaaa'}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ ml: 0.5 }}
    >
      Download
    </MuiLink>
  </Stack>
);

function UsedSnapshots({ projectId, snapshots }: { projectId: string; snapshots: Array<string | number> }) {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Snapshots
      </Typography>
      {!snapshots?.length ? (
        <Typography variant="body2" color="text.secondary">
          No historical snapshots used as context.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The following snapshots were used as context in the analysis:
          </Typography>
          <Stack component="nav" spacing={0.75}>
            {snapshots.map((id) => (
              <MuiLink
                key={String(id)}
                component={RouterLink}
                to={`/projects/${projectId}/snapshots/web/${id}`}
                underline="hover"
              >
                Snapshot {String(id)}
              </MuiLink>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

function TransactionsSection({
  title,
  items,
  headerHelp,
  bg
}: {
  title: string;
  headerHelp?: string;
  items: WebRequest[] | Array<{ r0: WebRequest; r1: WebRequest }>;
  bg?: string;
}) {
  const isPairs = Array.isArray(items) && items.length > 0 && (items as any)[0]?.r0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {title} {`(${items.length})`}
      </Typography>
      {headerHelp && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {headerHelp}
        </Typography>
      )}
      <Paper variant="outlined" sx={{ bgcolor: bg ?? grey[50], p: 1.25, overflow: 'auto' }}>
        {isPairs
          ? (items as Array<{ r0: WebRequest; r1: WebRequest }>).map(({ r0, r1 }, i) => {
              const matchContent = r0.response.sha256 === r1.response.sha256;
              return (
                <Box key={i} mb={1} borderBottom={`1px solid ${grey[300]}`} pb={1}>
                  <RequestRow req={r0} matchUrl={true} matchContent={matchContent} />
                  <RequestRow req={r1} matchUrl={true} matchContent={matchContent} />
                </Box>
              );
            })
          : (items as WebRequest[]).map((req, i) => (
              <RequestRow key={i} req={req} matchUrl={true} matchContent={true} />
            ))}
      </Paper>
    </Box>
  );
}

function HeuristicMatches({ pairs }: { pairs: Array<{ r0: WebRequest; r1: WebRequest }> }) {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Heuristically Matched Transactions ({pairs.length})
      </Typography>
      <Paper variant="outlined" sx={{ bgcolor: grey[100], p: 1.25, overflow: 'auto' }}>
        {pairs.map(({ r0, r1 }, i) => {
          const matchContent = r0.response.sha256 === r1.response.sha256;

          const path0 = new URL(r0.request.url).pathname.split('/').pop() ?? '';
          const path1 = new URL(r1.request.url).pathname.split('/').pop() ?? '';
          const len = Math.min(path0.length, path1.length);
          let shared = 0;
          while (shared < len && path0[shared] === path1[shared]) {
            shared++;
          }

          let cursor = shared - 1;
          while (cursor >= 0 && path0[cursor] !== '.' && path0[cursor] !== '-') {
            cursor--;
          }

          shared = cursor + 1;

          return (
            <Box key={i} mb={1} borderBottom={`1px solid ${grey[300]}`} pb={1}>
              <RequestRow req={r0} matchUrl={!!shared} matchContent={matchContent} />
              <RequestRow req={r1} matchUrl={!!shared} matchContent={matchContent} />
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
}

interface AlertDisplayProperties {
  alert: any;
}

const AlertDisplay: React.FC<AlertDisplayProperties> = ({ alert }) => {
  console.log(alert);
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {alert.type}
      </Typography>
    </Box>
  );
};

export function AnalysisDebugView() {
  const { projectId, analysisId, analysisType } = useParams<{
    projectId: string;
    analysisId: string;
    analysisType: AnalysisType;
  }>();

  if (!projectId || !analysisId || !analysisType) throw new Error('Missing parameter');

  const query = useAnalysisDebugQuery(projectId, analysisId, analysisType);

  if (query.isLoading) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton variant="text" width={320} height={40} />
          <Skeleton variant="rounded" width={160} height={36} />
        </Stack>
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={80} />
        <Skeleton variant="rounded" height={320} />
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <MuiAlert severity="error" variant="filled" sx={{ alignItems: 'flex-start' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Error loading debug analysis
        </Typography>
        <Typography variant="body2">{(query.error as any)?.message ?? 'Unknown error'}</Typography>
      </MuiAlert>
    );
  }

  const data = query.data;
  const matchDenominator = data.matches.length + data.missing.length;
  const matchRate = matchDenominator === 0 ? '—' : formatPercent(data.matches.length / matchDenominator);
  const perfect = data.matches.filter(({ r0, r1 }: { r0: WebRequest; r1: WebRequest }) => {
    try {
      return (
        new URL(r0.request.url).pathname === new URL(r1.request.url).pathname &&
        r0.response.sha256 !== r1.response.sha256
      );
    } catch {
      return false;
    }
  });

  const heuristic = data.matches.filter(({ r0, r1 }: { r0: WebRequest; r1: WebRequest }) => {
    try {
      return new URL(r0.request.url).pathname !== new URL(r1.request.url).pathname;
    } catch {
      return false;
    }
  });

  const stats = [
    { label: 'Responses Already Seen', value: data.seen.length },
    { label: 'Newly Observed Responses', value: data.fresh.length },
    { label: 'Exact Matches', value: data.matches.length },
    { label: 'Missing Responses', value: data.missing.length },
    { label: 'Additional Responses', value: data.additional.length },
    { label: 'Match Rate', value: matchRate }
  ];

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
      >
        <Stack>
          <Typography variant="h4">Web Analysis — Developer View</Typography>
          <Breadcrumbs aria-label="breadcrumbs" separator="/" sx={{ mt: 0.5 }}>
            <MuiLink component={RouterLink} to="/projects" underline="hover">
              Projects
            </MuiLink>
            <MuiLink component={RouterLink} to={`/projects/${projectId}`} underline="hover">
              {projectId}
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to={`/projects/${projectId}/analyses/web/${analysisId}`}
              underline="hover"
            >
              Analysis {analysisId}
            </MuiLink>
            <Typography color="text.secondary">Developer</Typography>
          </Breadcrumbs>
        </Stack>
        <Button
          component={RouterLink}
          to={`/projects/${projectId}/analyses/web/${analysisId}`}
          variant="outlined"
        >
          User View
        </Button>
      </Stack>

      {/* Metadata with Debug Summary */}
      <Box>
        <Typography variant="h5" gutterBottom>
          Metadata
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          divider={<Divider flexItem orientation="vertical" />}
        >
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Project
            </Typography>
            <MuiLink component={RouterLink} to={`/projects/${projectId}`} underline="hover">
              {projectId}
            </MuiLink>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Analysis ID
            </Typography>
            <Typography variant="body1">{analysisId}</Typography>
          </Stack>
          {stats.map((s) => (
            <Stack key={s.label} spacing={0.25}>
              <Typography variant="overline" color="text.secondary">
                {s.label}
              </Typography>
              <Typography variant="body1">{s.value}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Historical Context */}
      <UsedSnapshots projectId={projectId} snapshots={data.snapshots} />

      {/* Heuristic Matches */}
      <HeuristicMatches pairs={heuristic} />

      {/* Perfect Matches */}
      <TransactionsSection
        title={`Perfectly Matched Transactions with Different Body`}
        items={perfect}
        bg={grey[100]}
      />

      {/* Buckets */}
      <TransactionsSection title={`New Transactions Without a Match`} items={data.missing} bg={orange[50]} />
      <TransactionsSection
        title={`Old Transactions No Longer Present`}
        items={data.additional}
        bg={orange[50]}
      />
      <TransactionsSection
        title={`Transactions with Previously Unseen Responses`}
        items={data.fresh}
        bg={blue[50]}
      />
      <TransactionsSection
        title={`Transactions with Previously Seen Responses`}
        items={data.seen}
        bg={green[50]}
      />
    </Stack>
  );
}
